import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { LocalDate } from "@js-joda/core";
import React from "react";
import { formatNumberLocale } from "../../../format";
import { Paystub } from "../../../messages/Paystub";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { MEDFLYT_LOGO_ICON_ONLY } from "../../../utils/svgs_new";
import { AppIcon } from "../../Generic/AppIcon";

type Props = {
  paystub: Paystub;
};

const PaystubSummaryCard = (props: Props) => {
  const {
    checkDate,
    periodBeginDate,
    periodEndDate,
    totalGross,
    totalHours,
    netDirectDepositAmount,
    totalNet,
  } = props.paystub;
  const paymentMethod =
    netDirectDepositAmount !== 0 ? translate(STRINGS.DIRECT_DEPOSIT) : translate(STRINGS.CHECK);

  const formatLocalDate = (date: LocalDate) => globalize().formatLocalDate(date, { date: "short" });

  return (
    <CardRoot
      style={{ transformOrigin: "top" }}
      initial={{ opacity: 0, rotateX: 80, scaleX: 0.75, translateY: 100 }}
      animate={{ opacity: 1, rotateX: 0, scaleX: 1, translateY: 0 }}
      transition={{ type: "spring", delay: 0.1, duration: 1 }}
    >
      <CardHeader>
        <CardHeaderSection value={`${translate(STRINGS.PAID)} ${formatLocalDate(checkDate)}`} />
      </CardHeader>
      <CardContent>
        <CardTotalAmount>{`$${formatNumberLocale(totalNet)}`}</CardTotalAmount>
        <CardTotalAmountLabel>{`${translate(STRINGS.NET_PAY_FOR_PERIOD)}`}</CardTotalAmountLabel>
        <CardTotalAmountLabel>{`${formatLocalDate(periodBeginDate)} - ${formatLocalDate(
          periodEndDate
        )}`}</CardTotalAmountLabel>
      </CardContent>
      <CardFooter>
        <CardFooterSection>
          <CardFooterValue>{`$${formatNumberLocale(totalGross)}`}</CardFooterValue>
          <CardFooterValueLabel>{`${translate(STRINGS.GROSS_PAY)}`}</CardFooterValueLabel>
        </CardFooterSection>
        <CardFooterSection>
          <CardFooterValue>{`${totalHours}`}</CardFooterValue>
          <CardFooterValueLabel>{`${translate(STRINGS.HOURS)}`}</CardFooterValueLabel>
        </CardFooterSection>
        <CardFooterSection>
          <CardFooterValue>{`${paymentMethod}`}</CardFooterValue>
          <CardFooterValueLabel>{`${translate(STRINGS.PAYMENT_METHOD)}`}</CardFooterValueLabel>
        </CardFooterSection>
      </CardFooter>
      <CardLogoBackground>
        <AppIcon icon={MEDFLYT_LOGO_ICON_ONLY} fill="white" size={112} />
      </CardLogoBackground>
    </CardRoot>
  );
};

const CardRoot = styled(motion.div)`
  background: linear-gradient(260deg, var(--primary-color) 0%, #9108ff 100%);
  border-radius: 24px;
  padding: 24px;
  display: flex;
  height: 35vw;
  max-height: 192px;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;

  > * + * {
    border-left: 1px solid rgba(255, 255, 255, 0.25);
    padding-inline-start: 16px;
  }
`;

const CardHeaderSection = (props: { value: string }) => {
  return (
    <CardHeaderSectionRoot>
      <CardHeaderSectionValue>{props.value}</CardHeaderSectionValue>
    </CardHeaderSectionRoot>
  );
};
const CardHeaderSectionRoot = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const CardHeaderSectionValue = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 700;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CardTotalAmount = styled.div`
  color: white;
  font-size: 32px;
  line-height: 24px;
  font-weight: 800;
`;

const CardTotalAmountLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 500;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const CardFooterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CardFooterValue = styled.div`
  color: white;
  font-size: 16px;
  line-height: 24px;
  font-weight: 800;
`;

const CardFooterValueLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 500;
`;

const CardLogoBackground = styled.div`
  position: absolute;
  right: -8px;
  bottom: -16px;
  opacity: 0.1;
  pointer-events: none;

  svg {
    width: 112px;
  }
`;

export default PaystubSummaryCard;
